import { useState, useEffect, useCallback, useRef } from 'react';
import { cacheService, CacheConfig } from '../services/cacheService';

export interface UseOfflineDataOptions<T> {
  key: string;
  fetchFn: () => Promise<T>;
  maxAge?: number; // in seconds, default 5 minutes
  strategy?: 'cache-first' | 'network-first' | 'stale-while-revalidate';
  enabled?: boolean;
}

export interface UseOfflineDataReturn<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  isFromCache: boolean;
  isOffline: boolean;
  refresh: () => Promise<void>;
  clearCache: () => Promise<void>;
  lastUpdated: Date | null;
}

export function useOfflineData<T>(options: UseOfflineDataOptions<T>): UseOfflineDataReturn<T> {
  const {
    key,
    fetchFn,
    maxAge = 300, // 5 minutes default
    strategy = 'cache-first',
    enabled = true,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [isFromCache, setIsFromCache] = useState<boolean>(false);
  const [isOffline, setIsOffline] = useState<boolean>(() => {
    // Check if we're in a browser environment to avoid SSR crashes
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false; // Default to online during SSR
    }
    return !navigator.onLine;
  });
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const mountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Monitor online/offline status
  useEffect(() => {
    const cleanup = cacheService.onConnectivityChange((online) => {
      setIsOffline(!online);
      if (online && enabled) {
        // When coming back online, try to refresh data
        fetchData(false);
      }
    });

    return cleanup;
  }, [enabled]);

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!enabled || !mountedRef.current) return;

    // Cancel any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setError(null);

    try {
      const config: CacheConfig = {
        key,
        fetchFn: async () => {
          // Check if component is still mounted and request not aborted
          if (!mountedRef.current || abortControllerRef.current?.signal.aborted) {
            throw new Error('Request cancelled');
          }
          return await fetchFn();
        },
        maxAge,
        forceRefresh,
      };

      let result: T;
      let fromCache = false;

      switch (strategy) {
        case 'network-first':
          result = await cacheService.getNetworkFirst(config);
          break;
        case 'stale-while-revalidate': {
          result = await cacheService.getStaleWhileRevalidate(config);
          fromCache = true; // This strategy often returns cache first
          break;
        }
        case 'cache-first':
        default: {
          result = await cacheService.getCachedData(config);
          // Check if data came from cache using the public API
          const hasCache = await cacheService.hasCache(key);
          fromCache = hasCache && !forceRefresh;
          break;
        }
      }

      if (mountedRef.current) {
        setData(result);
        setIsFromCache(fromCache);
        setLastUpdated(new Date());
      }
    } catch (err) {
      if (mountedRef.current) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        if (error.message !== 'Request cancelled') {
          setError(error);
          console.error(`Failed to fetch data for ${key}:`, error);
        }
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [key, fetchFn, maxAge, strategy, enabled]);

  // Initial data fetch
  useEffect(() => {
    if (enabled) {
      fetchData();
    }

    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, enabled]);

  const refresh = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  const clearCache = useCallback(async () => {
    try {
      await cacheService.clearCache(key);
      if (enabled) {
        await fetchData(true);
      }
    } catch (err) {
      console.error(`Failed to clear cache for ${key}:`, err);
    }
  }, [key, enabled, fetchData]);

  return {
    data,
    loading,
    error,
    isFromCache,
    isOffline,
    refresh,
    clearCache,
    lastUpdated,
  };
}

// Hook for managing multiple data sources
// Note: This pattern violates Rules of Hooks and should be avoided.
// Instead, call useOfflineData individually for each data source you need.
// This function is deprecated and will be removed in a future version.
export function useOfflineDataCollection(
  configs: Array<UseOfflineDataOptions<any>>
): Record<string, UseOfflineDataReturn<any>> {
  // This implementation is fundamentally flawed and cannot be fixed
  // while maintaining React's Rules of Hooks
  console.warn(
    'useOfflineDataCollection violates React Rules of Hooks. ' +
    'Please use individual useOfflineData calls instead.'
  );
  
  const results: Record<string, UseOfflineDataReturn<any>> = {};
  
  // This still violates Rules of Hooks but is left for backward compatibility
  configs.forEach((config) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    results[config.key] = useOfflineData(config);
  });

  return results;
}

// Alternative pattern: Hook factory for specific use cases
// Example usage: const userData = useUserOfflineData(userId);
export function useUserOfflineData(userId: string) {
  return useOfflineData({
    key: `user-${userId}`,
    fetchFn: async () => {
      // Your user fetch logic here
      throw new Error('Implement user fetch logic');
    },
    maxAge: 300,
    strategy: 'cache-first',
    enabled: !!userId,
  });
}

// Example of how to properly use multiple data sources
// Instead of useOfflineDataCollection, do this in your component:
/*
const MyComponent = () => {
  const userData = useOfflineData({ key: 'user', fetchFn: fetchUser });
  const postsData = useOfflineData({ key: 'posts', fetchFn: fetchPosts });
  const settingsData = useOfflineData({ key: 'settings', fetchFn: fetchSettings });
  
  return (
    <div>
      {userData.loading && <div>Loading user...</div>}
      {postsData.loading && <div>Loading posts...</div>}
      {settingsData.loading && <div>Loading settings...</div>}
    </div>
  );
};
*/

// Hook for cache management utilities
export function useCacheManager() {
  const [cacheInfo, setCacheInfo] = useState<{ keys: string[]; size: number }>({
    keys: [],
    size: 0,
  });

  const refreshCacheInfo = useCallback(async () => {
    try {
      const info = await cacheService.getCacheInfo();
      setCacheInfo(info);
    } catch (error) {
      console.error('Failed to get cache info:', error);
    }
  }, []);

  const clearAllCache = useCallback(async () => {
    try {
      await cacheService.clearAllCache();
      await refreshCacheInfo();
    } catch (error) {
      console.error('Failed to clear all cache:', error);
    }
  }, [refreshCacheInfo]);

  useEffect(() => {
    refreshCacheInfo();
  }, [refreshCacheInfo]);

  return {
    cacheInfo,
    refreshCacheInfo,
    clearAllCache,
    isServiceWorkerSupported: cacheService.isServiceWorkerSupported(),
    isOffline: cacheService.isOffline(),
  };
} 